import {
  QueryObserverResult,
  UseMutationResult,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { APIClient } from 'api/APIClient';
import {
  ProjectSearchResults,
  SearchProjectOrEmployeeQuery,
} from 'api/types/types';
import { QueryKeys } from 'constants/queryKeys';

async function searchProject(
  query: SearchProjectOrEmployeeQuery
): Promise<ProjectSearchResults> {
  const { data } = await APIClient().post('/project/search', query);
  return data;
}

export function useSearchProjectQuery(): UseMutationResult<
  ProjectSearchResults,
  unknown,
  SearchProjectOrEmployeeQuery,
  unknown
> {
  return useMutation<
    ProjectSearchResults,
    unknown,
    SearchProjectOrEmployeeQuery,
    unknown
  >({
    mutationFn: (query: SearchProjectOrEmployeeQuery) => searchProject(query),
    mutationKey: [QueryKeys.PROJECT_SEARCH],
    meta: {
      errorMessage: 'Ekki tókst að sækja lista fyrir tiltekið verk.',
    },
  });
}

async function calculateDimensions<T>(
  emplId: string | undefined,
  projId?: string
): Promise<T> {
  if (typeof emplId === undefined && projId === undefined) {
    throw new Error('EmplId and ProjId is undefined');
  }
  const { data } = await APIClient().get(
    `/dimensions/calculate?emplId=${emplId}&projId=${projId}`
  );
  return data;
}

export function useGetCalculateDimensions(
  emplId?: string | undefined,
  projId?: string
): QueryObserverResult<ProjectSearchResults, unknown> {
  return useQuery<ProjectSearchResults, unknown>({
    queryKey: [QueryKeys.CALCULATE_DIMENSIONS, emplId, projId],
    queryFn: () => calculateDimensions<ProjectSearchResults>(emplId, projId),
    enabled: false,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

async function activitySearch(
  query: SearchProjectOrEmployeeQuery
): Promise<ProjectSearchResults> {
  const { data } = await APIClient().post('/activity/search', query);
  return data;
}

export function useActivitySearchQuery(): UseMutationResult<
  ProjectSearchResults,
  unknown,
  SearchProjectOrEmployeeQuery,
  unknown
> {
  return useMutation<
    ProjectSearchResults,
    unknown,
    SearchProjectOrEmployeeQuery,
    unknown
  >({
    mutationFn: (query: SearchProjectOrEmployeeQuery) => activitySearch(query),
    mutationKey: [QueryKeys.PROJECT_SEARCH],
    meta: {
      errorMessage: 'Ekki tókst að sækja víddir fyrir tiltekna verkbeiðni.',
    },
  });
}

async function departmentSearch(
  query: SearchProjectOrEmployeeQuery
): Promise<ProjectSearchResults> {
  const { data } = await APIClient().post(
    '/dimensions/departments/search',
    query
  );
  return data;
}

export function useDepartmentSearchQuery(): UseMutationResult<
  ProjectSearchResults,
  unknown,
  SearchProjectOrEmployeeQuery,
  unknown
> {
  return useMutation<
    ProjectSearchResults,
    unknown,
    SearchProjectOrEmployeeQuery,
    unknown
  >({
    mutationFn: (query: SearchProjectOrEmployeeQuery) =>
      departmentSearch(query),
    mutationKey: [QueryKeys.DEPARTMENT_SEARCH],
    meta: {
      errorMessage: 'Ekki tókst að sækja víddir fyrir tiltekna deild.',
    },
  });
}

async function locationSearch(
  query: SearchProjectOrEmployeeQuery
): Promise<ProjectSearchResults> {
  const { data } = await APIClient().post(
    '/dimensions/locations/search',
    query
  );
  return data;
}

export function useLocationSearchQuery(): UseMutationResult<
  ProjectSearchResults,
  unknown,
  SearchProjectOrEmployeeQuery,
  unknown
> {
  return useMutation<
    ProjectSearchResults,
    unknown,
    SearchProjectOrEmployeeQuery,
    unknown
  >({
    mutationFn: (query: SearchProjectOrEmployeeQuery) => locationSearch(query),
    mutationKey: [QueryKeys.LOCATION_SEARCH],
    meta: {
      errorMessage: 'Ekki tókst að sækja víddir fyrir tiltekna staðsetningu.',
    },
  });
}
